import { zodResolver } from "@hookform/resolvers/zod";
import { ListPlus, Plus } from "lucide-react";
import Link from "next/link";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { listService, useLists } from "@/api/list";
import { Customer, List, ListMember } from "@/api/types";
import { ApiError, handleApiError } from "@/api/utils";
import { Button, ButtonSize, ButtonVariant } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { ListsField } from "@/components/ui/form/lists-field";
import { getCustomerName } from "@/lib/utils";
import useActiveGym from "@/ui/hooks/active-gym";
import useMatchMutate from "@/ui/hooks/match-mutate";

interface AddToListModalProps {
  customer: Customer;
  listMemberships?: ListMember[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
}
const formSchema = z.object({
  lists: z.array(z.string()),
});
export function AddToListModal({
  customer,
  listMemberships,
  open,
  onOpenChange,
  onSuccess,
  variant = "outline",
  size = "sm",
  className = "",
}: AddToListModalProps) {
  const matchMutate = useMatchMutate();
  const { gym } = useActiveGym();
  const { data: lists, isLoading } = useLists({ gym: gym?.id, type: "static" });
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      lists: [],
    },
  });
  const listMembershipIds = listMemberships
    ? listMemberships.map((lm) => (lm.list as List)?.id).filter((id): id is string => Boolean(id))
    : [];
  const availableListsToJoin = lists?.filter((list) => !listMembershipIds.includes(list.id));
  async function onSubmit(values: z.infer<typeof formSchema>) {
    try {
      for (const list of values.lists) {
        await listService.createMembers(list, { customers: [customer.id] });
      }
      await matchMutate(/\/list-members/);
      onSuccess();
    } catch (err) {
      if (err instanceof ApiError) {
        handleApiError(err);
      }
    }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant={variant} size={size} className={className}>
          <ListPlus className="w-4 h-4 mr-1" aria-hidden="true" />
          Add to list
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Add to list</DialogTitle>
          <DialogDescription>
            Choose which list you want to add {getCustomerName(customer)} to.
          </DialogDescription>
        </DialogHeader>
        {!isLoading && availableListsToJoin && availableListsToJoin.length === 0 ? (
          <Card className="shadow-none">
            <CardContent className="rounded-b-md px-4 py-3">
              <div className="flex flex-col items-center justify-between gap-4 text-center">
                <div>
                  <h3 className="text-lg font-semibold leading-none">No available lists</h3>
                  <p className="text-muted-foreground mt-2 text-sm">
                    You have already added {getCustomerName(customer)} to all available lists.
                  </p>
                </div>
                <Button size="sm" className="w-full" asChild>
                  <Link href="/lists/new">
                    <Plus className="w-3 h-3 mr-1" aria-hidden="true" />
                    Create a new list
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
              <ListsField filteredIds={listMembershipIds} type="static" />
              <Button type="submit">Save changes</Button>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}
